import React, { useEffect, useRef } from 'react';

// Textarea that auto-grows to fit its content (and shrinks back as the
// operator deletes). Drop-in for any plain <textarea> — same prop API,
// just hand it the value/onChange like normal. minRows guards the
// initial / cleared height so the input doesn't collapse into a
// single-line strip when empty.
//
// Implementation: on every value change (or on mount), we reset the
// height to 'auto' so the browser recomputes scrollHeight from a clean
// baseline, then set the height to that scrollHeight. Without the
// reset-to-auto step the textarea only ever grows.
//
// Honours forwardRef so callers (e.g. focus management) can still get
// at the underlying DOM node.

const AutoSizeTextarea = React.forwardRef(function AutoSizeTextarea(
  { value, onChange, minRows = 3, className = '', style, ...rest },
  forwardedRef,
) {
  const localRef = useRef(null);
  // Expose the same node both internally (for resize) and externally
  // (for the consumer's ref, if they passed one).
  const setRef = (node) => {
    localRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  useEffect(() => {
    const el = localRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={setRef}
      value={value}
      onChange={onChange}
      rows={minRows}
      className={className}
      style={{ overflow: 'hidden', resize: 'none', ...style }}
      {...rest}
    />
  );
});

export default AutoSizeTextarea;
